import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Button, 
  Typography, 
  Box, 
  Alert,
  Stack
} from '@mui/material';
import { 
  InstallMobile as InstallMobileIcon,
  QrCode as QrCodeIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import backgroundImage from "../images/Einfahrt_B811.jpeg";

export const LandingQRCode = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [debugInfo, setDebugInfo] = useState({});

  useEffect(() => {
    // Collect debug information
    const debug = {
      userAgent: navigator.userAgent,
      isSecure: window.location.protocol === 'https:' || window.location.hostname === 'localhost',
      hasServiceWorker: 'serviceWorker' in navigator,
      standalone: window.matchMedia('(display-mode: standalone)').matches,
      url: window.location.href,
      isOnline: navigator.onLine,
      connection: navigator.connection ? navigator.connection.effectiveType : 'unknown'
    };
    setDebugInfo(debug);
    console.log('PWA Debug Info:', debug);

    // Check PWA criteria with a delay to allow service worker registration
    const checkPWAReadiness = () => {
      setTimeout(() => {
        console.log('Checking PWA readiness after delay...');
        if (navigator.serviceWorker) {
          navigator.serviceWorker.ready.then(() => {
            console.log('Service Worker is ready');
          });
        }
      }, 2000);
    };
    checkPWAReadiness();

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('✅ beforeinstallprompt event fired!', e);
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Enhanced debugging for PWA readiness
    const checkManifest = async () => {
      try {
        const response = await fetch('/manifest.json');
        const manifest = await response.json();
        console.log('✅ Manifest loaded successfully:', manifest);
        
        // Check if manifest has required fields
        const requiredFields = ['name', 'short_name', 'start_url', 'display', 'icons'];
        const missingFields = requiredFields.filter(field => !manifest[field]);
        if (missingFields.length > 0) {
          console.error('❌ Missing required manifest fields:', missingFields);
        } else {
          console.log('✅ All required manifest fields present');
        }
        
        // Check icons
        if (manifest.icons && manifest.icons.length > 0) {
          console.log('✅ Manifest has', manifest.icons.length, 'icons');
          manifest.icons.forEach((icon, index) => {
            console.log(`Icon ${index + 1}:`, icon.src, icon.sizes, icon.purpose || 'any');
          });
        } else {
          console.error('❌ No icons in manifest');
        }
      } catch (error) {
        console.error('❌ Error loading manifest:', error);
      }
    };

    setTimeout(checkManifest, 1000);

    // Force check PWA installability after a delay
    const forceInstallabilityCheck = () => {
      setTimeout(() => {
        console.log('🔍 Forcing installability check...');
        
        // Check if we missed the beforeinstallprompt
        if (!deferredPrompt && !isInstalled) {
          console.log('⚠️ beforeinstallprompt not fired. Reasons could be:');
          console.log('1. App already installed (check chrome://apps/)');
          console.log('2. Browser criteria not met');
          console.log('3. User previously dismissed installation');
          console.log('4. Browser cache needs clearing');
          
          // Try to manually check installability
          if ('getInstalledRelatedApps' in navigator) {
            navigator.getInstalledRelatedApps().then(apps => {
              console.log('Installed related apps:', apps);
              if (apps.length > 0) {
                console.log('✅ App might already be installed');
                setIsInstalled(true);
              }
            });
          }
        }
      }, 5000); // Wait 5 seconds
    };
    
    forceInstallabilityCheck();

    // Check if app is already installed
    const handleAppInstalled = () => {
      console.log('✅ App installed event fired');
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Check if running as PWA
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) {
      console.log('App is running in standalone mode (already installed)');
      setIsInstalled(true);
    }

    // Log current state
    console.log('Initial PWA state:', {
      isInstallable: false,
      isInstalled: debug.standalone,
      hasServiceWorker: debug.hasServiceWorker,
      isSecure: debug.isSecure
    });

    // Additional PWA checks
    if (debug.hasServiceWorker) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        console.log('Service Worker registrations:', registrations.length);
        registrations.forEach((registration, index) => {
          console.log(`SW ${index + 1}:`, registration.scope);
        });
      });
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    console.log(`User response to the install prompt: ${outcome}`);
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    // Clear the deferredPrompt variable
    setDeferredPrompt(null);
    setIsInstallable(false);
  };

  const getInstallInstructions = () => {
    const userAgent = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && !/Chrome/.test(userAgent);
    
    if (isIOS && isSafari) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Für iOS Safari:</strong><br/>
            1. Tippen Sie auf das Teilen-Symbol <strong>⬆️</strong><br/>
            2. Scrollen Sie nach unten und wählen Sie <strong>"Zum Home-Bildschirm"</strong><br/>
            3. Tippen Sie auf <strong>"Hinzufügen"</strong>
          </Typography>
        </Alert>
      );
    }
    
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Manuelle Installation:</strong><br/>
          • <strong>Chrome Android:</strong> Menü → "App installieren"<br/>
          • <strong>Chrome Desktop:</strong> Adressleiste → Install-Symbol<br/>
          • <strong>Firefox:</strong> Menü → "Als App installieren"
        </Typography>
      </Alert>
    );
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        height: 'auto',
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'auto',
        backgroundPosition: 'top left',
        backgroundRepeat: 'repeat',
        backgroundAttachment: 'scroll',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        padding: 2,
        position: 'relative',
        zIndex: 1,
        paddingTop: '2rem'
      }}
    >
      {/* Header Card */}
      <Card sx={{ maxWidth: 450, width: '100%', borderRadius: 3, boxShadow: 4, backgroundColor: 'rgba(255, 255, 255, 0.85)', mb: 2 }}>
        <CardContent sx={{ textAlign: 'center', p: 3 }}>
          <Box sx={{ mb: 2 }}>
            <QrCodeIcon sx={{ fontSize: 60, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" component="h1" gutterBottom color="primary">
              Willkommen!
            </Typography>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              InfoGig Web App
            </Typography>
          </Box>
          
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Sie haben einen QR-Code gescannt und sind jetzt bereit, die InfoGig Web App 
            auf Ihrem Gerät zu installieren. Mit der Installation haben Sie schnellen 
            Zugriff auf alle Fahrpläne und Funktionen.
          </Typography>
        </CardContent>
      </Card>

      {/* Installation Status Card */}
      {isInstalled ? (
        <Card sx={{ maxWidth: 450, width: '100%', borderRadius: 3, boxShadow: 4, backgroundColor: 'rgba(255, 255, 255, 0.85)', mb: 2 }}>
          <CardContent sx={{ textAlign: 'center', p: 3 }}>
            <Alert severity="success">
              <Typography variant="body2">
                ✅ App ist bereits installiert! Sie können sie über Ihren Home-Bildschirm öffnen.
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Installation Button Card */}
          {isInstallable ? (
            <Card sx={{ maxWidth: 450, width: '100%', borderRadius: 3, boxShadow: 4, backgroundColor: 'rgba(255, 255, 255, 0.85)', mb: 2 }}>
              <CardContent sx={{ textAlign: 'center', p: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<InstallMobileIcon />}
                  onClick={handleInstallClick}
                  sx={{ 
                    py: 1.5, 
                    fontSize: '1.1rem',
                    width: '100%',
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #1976D2 30%, #0288D1 90%)',
                    }
                  }}
                >
                  App jetzt installieren
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Installation Not Available Card */}
              <Card sx={{ maxWidth: 450, width: '100%', borderRadius: 3, boxShadow: 4, backgroundColor: 'rgba(255, 255, 255, 0.85)', mb: 2 }}>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<PhoneIcon />}
                    disabled
                    sx={{ py: 1.5, fontSize: '1.1rem', width: '100%' }}
                  >
                    Installation nicht verfügbar
                  </Button>
                  
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={() => {
                      console.log('🔄 Manual PWA check triggered');
                      window.location.reload();
                    }}
                    sx={{ mt: 2, width: '100%' }}
                  >
                    Installierbarkeit erneut prüfen
                  </Button>
                </CardContent>
              </Card>

              {/* Debug Information Card */}
              <Card sx={{ maxWidth: 450, width: '100%', borderRadius: 3, boxShadow: 4, backgroundColor: 'rgba(255, 255, 255, 0.85)', mb: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Alert severity="info" sx={{ textAlign: 'left' }}>
                    <Typography variant="body2">
                      <strong>PWA Status Check:</strong><br/>
                      • HTTPS/Localhost: {debugInfo.isSecure ? '✅' : '❌'}<br/>
                      • Service Worker: {debugInfo.hasServiceWorker ? '✅' : '❌'}<br/>
                      • Browser Support: {/Chrome|Edge|Samsung/.test(debugInfo.userAgent) ? '✅ Chrome/Edge' : '❌ Nicht unterstützt'}<br/>
                      • Bereits installiert: {debugInfo.standalone ? '✅' : '❌'}<br/>
                      • Online: {debugInfo.isOnline ? '✅' : '❌'}<br/>
                      • beforeinstallprompt: {deferredPrompt ? '✅ Verfügbar' : '❌ Nicht ausgelöst'}<br/>
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>

              {/* Manual Installation Instructions Card */}
              <Card sx={{ maxWidth: 450, width: '100%', borderRadius: 3, boxShadow: 4, backgroundColor: 'rgba(255, 255, 255, 0.85)', mb: 2 }}>
                <CardContent sx={{ p: 3 }}>
                  <Alert severity="warning">
                    <Typography variant="body2">
                      <strong>Manuelle Installation (Chrome Android):</strong><br/>
                      1. Chrome Menü öffnen (⋮)<br/>
                      2. "App installieren" oder "Zum Startbildschirm hinzufügen" wählen<br/>
                      3. "Installieren" bestätigen
                    </Typography>
                  </Alert>
                </CardContent>
              </Card>
            </>
          )}

          {/* Platform Specific Instructions Card */}
          <Card sx={{ maxWidth: 450, width: '100%', borderRadius: 3, boxShadow: 4, backgroundColor: 'rgba(255, 255, 255, 0.85)', mb: 2 }}>
            <CardContent sx={{ p: 3 }}>
              {getInstallInstructions()}
            </CardContent>
          </Card>
        </>
      )}

      {/* Access Web Version Card */}
      <Card sx={{ maxWidth: 450, width: '100%', borderRadius: 3, boxShadow: 4, backgroundColor: 'rgba(255, 255, 255, 0.85)', mb: 2 }}>
        <CardContent sx={{ textAlign: 'center', p: 3 }}>
          <Button
            variant="outlined"
            size="large"
            href="/"
            sx={{ 
              py: 1.2,
              width: '100%',
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
              }
            }}
          >
            {isInstalled ? 'Web-Version öffnen' : 'Direkt zur Web-Version'}
          </Button>
        </CardContent>
      </Card>

      {/* Features Card */}
      <Card sx={{ maxWidth: 450, width: '100%', borderRadius: 3, boxShadow: 4, backgroundColor: 'rgba(255, 255, 255, 0.85)', mb: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle2" color="primary" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
            Was Sie erwartet:
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div" sx={{ textAlign: 'left' }}>
            • 📅 Aktuelle Fahrpläne<br/>
            • 🔍 Intelligente Plansuche mit Datum und Dienstnummer<br/>
            • 📱 Offline-Zugriff nach Installation<br/>
            • 🚀 Schneller Zugriff vom Home-Bildschirm<br/>
            • 📋 Umleitungen und wichtige Informationen
          </Typography>
        </CardContent>
      </Card>

      {/* Footer Card */}
      <Card sx={{ maxWidth: 450, width: '100%', borderRadius: 3, boxShadow: 4, backgroundColor: 'rgba(255, 255, 255, 0.75)' }}>
        <CardContent sx={{ textAlign: 'center', p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            InfoGig Web App • QR-Code Landing
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};
